import { API_BASE } from '../config/environment';

/**
 * Authenticated API utility for making requests with Keycloak bearer tokens
 * Supports both SPA flow (with token) and bypass mode (without token)
 */

// Get the current auth context - this will be injected by components
let authContext = null;

export const setAuthContext = (context) => {
  authContext = context;
};

/**
 * Create authenticated fetch request with bearer token
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {


  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // DEBUG VERSION CHECK
  if (!window._api_debug_logged) {
    console.log('[API] Loaded updated api.js with suppressAuthError support (v1.0.1+)', new Date().toISOString());
    window._api_debug_logged = true;
  }

  // Add authorization header if we have a token
  if (authContext && authContext.getToken) {
    try {
      const token = authContext.getToken();
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
      }
    } catch (error) {
      console.error('[API_FETCH] Failed to get auth token:', error);
      // Continue without token - might be in bypass mode
    }
  } else {
  }



  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include', // Required for CORS with Authorization headers
  };

  return fetch(url, fetchOptions);
};

/**
 * Make authenticated GET request with automatic token refresh on 401
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiGet = async (endpoint, optionsOrRetry = 0) => {
  // Normalize arguments to support both (endpoint, retryCount) and (endpoint, options)
  let retryCount = 0;
  let options = {};
  
  if (typeof optionsOrRetry === 'number') {
    retryCount = optionsOrRetry;
  } else if (typeof optionsOrRetry === 'object') {
    options = optionsOrRetry;
    retryCount = options.retryCount || 0;
  }

  const response = await authenticatedFetch(`${API_BASE}${endpoint}`);
  
  // Handle 401 errors with token refresh retry
  if (response.status === 401 && retryCount === 0) {
    
    if (authContext && authContext.refreshToken) {
      try {
        await authContext.refreshToken();
        
        // Retry the request once with the new token
        // Use recursive call with updated retryCount in options or direct argument
        const nextRetry = retryCount + 1;
        const nextArgs = typeof optionsOrRetry === 'object' 
          ? { ...options, retryCount: nextRetry } 
          : nextRetry;
          
        return apiGet(endpoint, nextArgs);
      } catch (refreshError) {
        console.error('[API_GET] Token refresh failed:', refreshError);
        // Only trigger global handler if NOT suppressed
        if (!options.suppressAuthError && authContext.handleSessionExpired) {
          authContext.handleSessionExpired();
        }
        throw new Error(`API Error 401: Authentication expired - ${refreshError.message}`);
      }
    } else {
      console.error('[API_GET] No refresh token available');
      if (!options.suppressAuthError && authContext && authContext.handleSessionExpired) {
        authContext.handleSessionExpired();
      }
      throw new Error('API Error 401: Authentication expired');
    }
  }
  
  if (!response.ok) {
    if (response.status === 401) {
      if (!options.suppressAuthError && authContext && authContext.handleSessionExpired) {
        authContext.handleSessionExpired();
      }
    }
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }
  
  return response.json();
};

/**
 * Make authenticated POST request with automatic token refresh on 401
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {any} data - Request body data
 * @param {string} method - HTTP method (default: 'POST')
 * @param {string} responseType - Response type: 'json', 'blob', 'text' (default: 'json')
 * @param {number} retryCount - Internal retry counter (default: 0)
 * @returns {Promise<any>} - Parsed response based on responseType
 */
export const apiPost = async (endpoint, data = null, method = 'POST', responseType = 'json', retryCount = 0) => {
  const options = {
    method: method,
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  const response = await authenticatedFetch(`${API_BASE}${endpoint}`, options);
  
  // Handle 401 errors with token refresh retry
  if (response.status === 401 && retryCount === 0) {
    
    if (authContext && authContext.refreshToken) {
      try {
        await authContext.refreshToken();
        
        // Retry the request once with the new token
        return apiPost(endpoint, data, method, responseType, retryCount + 1);
      } catch (refreshError) {
        console.error('[API_POST] Token refresh failed:', refreshError);
        if (authContext.handleSessionExpired) authContext.handleSessionExpired();
        throw new Error(`API Error 401: Authentication expired - ${refreshError.message}`);
      }
    } else {
      console.error('[API_POST] No refresh token available');
      if (authContext && authContext.handleSessionExpired) authContext.handleSessionExpired();
      throw new Error('API Error 401: Authentication expired');
    }
  }
  
  if (!response.ok) {
    if (response.status === 401 && authContext && authContext.handleSessionExpired) {
      authContext.handleSessionExpired();
    }
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }
  
  // Handle different response types
  switch (responseType) {
    case 'blob':
      return response.blob();
    case 'text':
      return response.text();
    case 'json':
    default:
      return response.json();
  }
};

/**
 * Make authenticated PUT request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {any} data - Request body data
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiPut = async (endpoint, data) => {
  const response = await authenticatedFetch(`${API_BASE}${endpoint}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }
  
  return response.json();
};

/**
 * Make authenticated DELETE request
 * @param {string} endpoint - API endpoint (without base URL)
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiDelete = async (endpoint) => {
  const response = await authenticatedFetch(`${API_BASE}${endpoint}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }
  
  return response.json();
};

/**
 * Refresh token if needed before making API calls
 * @returns {Promise<boolean>} - True if token is valid/refreshed, false otherwise
 */
export const ensureValidToken = async () => {
  if (!authContext || !authContext.refreshToken) {
    return true; // Bypass mode or no auth context
  }

  try {
    await authContext.refreshToken();
    return true;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return false;
  }
};

/**
 * Handle API errors consistently across the application
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred
 */
export const handleApiError = (error, context = 'API call') => {
  console.error(`${context} failed:`, error);
  
  // Check if it's an authentication error
  if (error.message.includes('401') || error.message.includes('Unauthorized')) {
    // Could trigger re-authentication here if needed
  }
  
  return error;
};
